import { createFileRoute, notFound } from '@tanstack/react-router';

import { buildGuards, hasFlagEnabled } from '@mint-lib/routing-guards';
import { Campaign } from '@mint-sdk/api-campaign-collections';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { Rows as MediaRow } from '@mint-sdk/api-media-row';
import { Import, ImportRule, Settings } from '@mint-sdk/api-omni';
import { z } from 'zod';

const staleTime = 1000 * 60 * 5;

const restrictedPlatforms = {
  guardOmniConnectorCriteo: buildGuards(hasFlagEnabled('omniConnectorCriteo')),
  guardOmniConnectorTradeDesk: buildGuards(
    hasFlagEnabled('omniConnectorTradeDesk'),
  ),
  guardOmniConnectorXads: buildGuards(hasFlagEnabled('omniConnectorXads')),
};

const guards = [
  {
    guard: restrictedPlatforms.guardOmniConnectorCriteo,
    pathname: 'criteo_integrated',
  },
  {
    guard: restrictedPlatforms.guardOmniConnectorTradeDesk,
    pathname: 'trade_desk_integrated',
  },
  {
    guard: restrictedPlatforms.guardOmniConnectorXads,
    pathname: 'x_ads_integrated',
  },
];

export const Route = createFileRoute(
  '/_secured/campaign/$campaignId/settings/omni/$platform/$tileId/settings',
)({
  async beforeLoad({ context, location }) {
    const guardsContextPromise = context.getGuardContext(
      await context.getInstance('flagsmith', '@mint/arm'),
    );

    const guardsContext = await guardsContextPromise;

    if (
      guards.some(
        (platformGuard) =>
          !platformGuard.guard(guardsContext) &&
          location.pathname.includes(platformGuard.pathname),
      )
    ) {
      throw notFound();
    }
  },
  async loader({ context, params, location }) {
    const queryClient =
      await context.serviceLocator.getOrThrowInstance('queryClient');

    const isSettingsMint2Enabled = await context.isFlagEnabled(
      '@mint/arm',
      'settingsMint2',
    );

    const tileSettings = await queryClient.ensureQueryData({
      ...Import.getTileSettingsList({
        params: { tileId: params.tileId },
      }),
      staleTime,
    });

    const platformConfiguration = await queryClient.ensureQueryData({
      ...Import.getPlatformConfiguration({
        params: { platform: params.platform },
      }),
      staleTime,
    });

    if (tileSettings?.id) {
      //Prefetch campaign columns
      queryClient.ensureQueryData({
        ...Settings.fetchCampaignsColumns({
          urlParams: {
            tileSettingsId: tileSettings.id,
          },
          params: {
            platform: params.platform,
          },
        }),
        staleTime,
      });

      //Prefetch ad group columns
      queryClient.ensureQueryData({
        ...Settings.fetchAdGroupsColumns({
          urlParams: {
            tileSettingsId: tileSettings.id,
          },
          params: {
            platform: params.platform,
          },
        }),
        staleTime,
      });
      //Prefetch ad columns
      queryClient.ensureQueryData({
        ...Settings.fetchAdColumns({
          urlParams: {
            tileSettingsId: tileSettings.id,
          },
          params: {
            platform: params.platform,
          },
        }),
        staleTime,
      });
    }

    if (isSettingsMint2Enabled) {
      queryClient.ensureQueryData({
        ...MediaPlan.getMediaItemDetailsQueryOptions({
          urlParams: { mediaItemId: params.tileId },
        }),
        staleTime,
      });
    } else {
      queryClient.ensureQueryData({
        ...Campaign.getCampaignQueryOptions({
          urlParams: { campaignId: params.campaignId },
        }),
        staleTime,
      });
      queryClient.ensureQueryData({
        ...MediaRow.getDetails({
          urlParams: { rowId: params.tileId },
        }),
        staleTime,
      });
    }

    if (
      tileSettings?.id &&
      platformConfiguration?.data?.items[0]?.autoImportEnabled
    ) {
      //Auto import prefetch
      queryClient.ensureQueryData({
        ...ImportRule.getImportRuleList({
          params: {
            tileSettingsId: tileSettings.id,
          },
        }),
        staleTime,
      });
    }
  },
  validateSearch: z.object({
    tab: z.string().optional(),
    search: z.string().optional(),
    mediaPlan: z.union([z.string(), z.number()]).optional(),
    activity: z.string().optional(),
    flight: z.string().optional(),
  }),
});
